// app/components/privacy-graph-scrolly/static-end-state.tsx
//
// SSR-safe end-state of the privacy graph, used as fallback for:
//   - Viewports < 1024px
//   - SSR pre-hydration
//   - JS-disabled clients
//
// Renders the fully-resolved end state (all 6 focused nodes including Bedrock
// with drift stroke; DDB lit + finding chip; DDB Snapshot affected; finding
// modal beside the graph). No animation, no client-only code.

import {
  FINDING_MODAL_DATA,
  FEATURED_WORKLOAD,
} from "./keyframe-data";
import {
  LAYOUT,
  EDGE,
  ZONE,
  FINDING_STROKE,
  PERSONAL_DATA_NODES,
  FINDING_NODE,
  AFFECTED_NODE,
  MOBILE,
  polylinePath,
  getNode,
} from "./graph-layout";
import { TierLanes } from "./elements/tier-lanes";
import { WorkloadZone } from "./elements/workload-zone";
import { NodeCard } from "./elements/node-card";
import { Modal } from "./elements/modal";
import { ClassificationChip, classificationChipWidth } from "./elements/classification-chip";
import { FindingChip, findingChipWidth } from "./elements/finding-chip";
import { SVG_COLORS } from "@/lib/svg-colors";

const FINDING_LABEL = "No Retention Period";
const PERSONAL_DATA_LABEL = "PERSONAL DATA";
const MODAL_W = 320;

function nodeStateFor(id: string): "default" | "drift" | "focus" | "affected" {
  if (id === "bedrock") return "drift";
  if (id === FINDING_NODE) return "focus";
  if (id === AFFECTED_NODE) return "affected";
  return "default";
}

function mobileEdgePath(points: readonly [number, number][]): string {
  return points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x},${y}`).join(" ");
}

function MobileFlowEdge({
  d,
  lit = false,
  drift = false,
}: {
  d: string;
  lit?: boolean;
  drift?: boolean;
}) {
  return (
    <path
      d={d}
      fill="none"
      stroke={drift ? SVG_COLORS.risk : lit ? FINDING_STROKE : EDGE.flow}
      strokeWidth={lit || drift ? EDGE.flowLitWidth : EDGE.flowWidth}
      strokeDasharray={EDGE.copyDash}
      strokeLinejoin="round"
      strokeLinecap="round"
      markerEnd={
        drift
          ? "url(#mobile-static-arrow-drift)"
          : lit
            ? "url(#mobile-static-arrow-flow-lit)"
            : "url(#mobile-static-arrow-flow)"
      }
    />
  );
}

function StaticMobileEndState() {
  return (
    <svg
      viewBox={`0 0 ${MOBILE.viewBox.w} ${MOBILE.viewBox.h}`}
      width="100%"
      className="block w-full"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Privacy graph end state — finding surfaced on DynamoDB"
    >
      <defs>
        <marker
          id="mobile-static-arrow-flow"
          viewBox="0 0 10 10"
          refX={8}
          refY={5}
          markerWidth={8}
          markerHeight={8}
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill="rgba(74,83,97,0.55)" />
        </marker>
        <marker
          id="mobile-static-arrow-flow-lit"
          viewBox="0 0 10 10"
          refX={8}
          refY={5}
          markerWidth={8}
          markerHeight={8}
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.drift} />
        </marker>
        <marker
          id="mobile-static-arrow-drift"
          viewBox="0 0 10 10"
          refX={8}
          refY={5}
          markerWidth={8}
          markerHeight={8}
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.risk} />
        </marker>
      </defs>
      <rect
        x={MOBILE.zone.x}
        y={MOBILE.zone.y}
        width={MOBILE.zone.w}
        height={MOBILE.zone.h}
        rx={ZONE.radius}
        fill={ZONE.fill}
        stroke={ZONE.stroke}
        strokeWidth={ZONE.strokeWidth}
        strokeDasharray={ZONE.dash}
      />

      <MobileFlowEdge d={mobileEdgePath([[180, 104], [180, 132]])} />
      <MobileFlowEdge d={mobileEdgePath([[180, 194], [180, 210], [70, 210], [70, 304], [99, 340]])} />
      <MobileFlowEdge d={mobileEdgePath([[180, 194], [180, 210], [290, 210], [290, 304], [261, 340]])} />
      <MobileFlowEdge d={mobileEdgePath([[261, 402], [261, 456]])} lit />
      <MobileFlowEdge d={mobileEdgePath([[180, 194], [180, 232]])} drift />

      <NodeCard id="api-gw" x={105} y={42} />
      <NodeCard id="lambda" x={105} y={132} />
      <NodeCard id="bedrock" x={105} y={232} state="drift" />
      <NodeCard id="s3" x={24} y={340} state="workload" />
      <NodeCard id="dynamo" x={186} y={340} state="focus" />
      <NodeCard id="ddb-snap" x={186} y={456} state="affected" />
      <Modal data={FINDING_MODAL_DATA} x={MOBILE.modal.x} y={MOBILE.modal.y} w={MOBILE.modal.w} />
    </svg>
  );
}

export function StaticEndState() {
  const ddb = getNode(FINDING_NODE);
  const chipW = findingChipWidth(FINDING_LABEL);
  const chipX = ddb.cx - chipW / 2;
  const chipY = ddb.y + ddb.h + 14;

  return (
    <div
      className="bg-secondary py-4 sm:py-6 [[data-scrolly-active=true]_&]:hidden"
      data-scrolly-fallback="true"
    >
      <div className="bg-dot-grid-16 sm:hidden">
        <StaticMobileEndState />
      </div>
      <div className="bg-dot-grid-16 hidden -mx-2 overflow-x-auto px-2 pb-2 sm:mx-0 sm:block sm:overflow-visible sm:px-0 sm:pb-0">
        <svg
          viewBox={`0 0 ${LAYOUT.viewBox.w} ${LAYOUT.viewBox.h}`}
          width="100%"
          className="min-w-[760px] sm:min-w-0"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Privacy graph end state — finding surfaced on DynamoDB"
        >
          <defs>
            <marker
              id="static-arrow-flow"
              viewBox="0 0 10 10"
              refX={8}
              refY={5}
              markerWidth={8}
              markerHeight={8}
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill="rgba(74,83,97,0.55)" />
            </marker>
            <marker
              id="static-arrow-flow-lit"
              viewBox="0 0 10 10"
              refX={8}
              refY={5}
              markerWidth={8}
              markerHeight={8}
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.drift} />
            </marker>
            <marker
              id="static-arrow-drift"
              viewBox="0 0 10 10"
              refX={8}
              refY={5}
              markerWidth={8}
              markerHeight={8}
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill={SVG_COLORS.risk} />
            </marker>
          </defs>
          <TierLanes showAiml={true} />
          <WorkloadZone label={FEATURED_WORKLOAD.label} />

          {LAYOUT.edges.map((e) => {
            const isLitCopy = e.from === FINDING_NODE && e.to === AFFECTED_NODE;
            const markerId = isLitCopy ? "static-arrow-flow-lit" : "static-arrow-flow";
            return (
              <path
                key={e.id}
                d={polylinePath(e.points)}
                fill="none"
                stroke={isLitCopy ? EDGE.flowLit : EDGE.flow}
                strokeWidth={isLitCopy ? EDGE.flowLitWidth : EDGE.flowWidth}
                strokeDasharray={EDGE.copyDash}
                strokeLinejoin="round"
                strokeLinecap="round"
                markerEnd={`url(#${markerId})`}
              />
            );
          })}

          {LAYOUT.nodes.map((n) => (
            <NodeCard
              key={n.id}
              id={n.id as Parameters<typeof NodeCard>[0]["id"]}
              x={n.x}
              y={n.y}
              w={n.w}
              state={nodeStateFor(n.id)}
            />
          ))}

          {PERSONAL_DATA_NODES.map((id) => {
            const n = getNode(id);
            const w = classificationChipWidth(PERSONAL_DATA_LABEL);
            return (
              <ClassificationChip
                key={`chip-${id}`}
                x={n.cx - w / 2}
                y={n.y - 28}
                label={PERSONAL_DATA_LABEL}
              />
            );
          })}

          <FindingChip x={chipX} y={chipY} label={FINDING_LABEL} />
          <Modal data={FINDING_MODAL_DATA} x={40} y={36} w={MODAL_W} />
        </svg>
      </div>
    </div>
  );
}
